import type { Family, Member } from '../../types';

// The shape of the bulk-upload workbook, defined once.
//
// It mirrors the two ways the app adds a person, because those are the only two
// ways a record can come into being:
//
//   + Add ▸ 👤 Head    creates a household AND its head, in one save
//   + Add ▸ 👥 Member  puts a person into a household that already exists
//
// So there is a Heads sheet and a Members sheet, and one row is one form. There
// is no "create a family" step in the app and none here either — a household
// exists because a Head row created it, which is why Address and Parish live on
// the Heads sheet beside the head's own details.
//
// That also makes the register's central rule structural rather than checked: a
// household IS a row on the Heads sheet, so it cannot have two heads or none.
//
// Both directions read this file — the template is written from it and the
// uploaded file is read back through it — so a heading cannot be renamed on one
// side only. `key` matches the field name the import API expects.

export interface SheetColumn {
  /** Exactly what the clerk sees in row 1. Matched case-insensitively on read. */
  header: string;
  /** Field name on the import payload. */
  key: string;
  /**
   * Required to CREATE. On an update a blank cell means "leave it alone", so
   * demanding it there would contradict that rule and reject a register that
   * predates the field.
   */
  required?: boolean;
  width: number;
  /** Values offered as an Excel dropdown, and checked on read. */
  options?: readonly string[];
  /**
   * Forces the cell to text. Excel otherwise strips the leading digit from a
   * mobile number and renders a 12-digit Aadhaar as 4.32157E+11 — the columns
   * where that silently destroys real data.
   */
  text?: boolean;
  /** Belongs to the household, not the person. Heads sheet only. */
  household?: boolean;
  /** Shown under the header as a note in the template. */
  hint?: string;
}

export const GENDERS = ['Male', 'Female'] as const;

/**
 * What a non-head can be. `Head` is deliberately absent: a head is created by
 * adding a row to the Heads sheet, so it cannot be conjured here — which is what
 * makes a second head impossible rather than merely rejected.
 */
export const RELATIONS = ['Spouse', 'Son', 'Daughter', 'Father', 'Mother', 'Other'] as const;

export const MARITAL_STATUSES = ['Single', 'Married', 'Widowed', 'Divorced'] as const;
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const;
export const LIFE_STATUSES = ['Alive', 'Inactive', 'Deceased', 'Transferred'] as const;
export const COMMUNION_STATUSES = ['Received', 'Not Received', 'In Preparation'] as const;
export const FAMILY_STATUSES = ['ACTIVE', 'TRANSFERRED', 'INACTIVE'] as const;

export const HEAD_SHEET = 'Heads';
export const MEMBER_SHEET = 'Members';
export const REFERENCE_SHEET = 'Reference';

/** The columns every person carries, head or not. */
const PERSON_TAIL: readonly SheetColumn[] = [
  { header: 'Father / Husband Name', key: 'relativeName', width: 24 },
  { header: 'Aadhaar', key: 'aadhaarNumber', width: 16, text: true, hint: '12 digits' },
  { header: 'Blood Group', key: 'bloodGroup', width: 13, options: BLOOD_GROUPS },
  { header: 'Education', key: 'education', width: 18 },
  { header: 'Occupation', key: 'occupation', width: 18 },
  { header: 'Location', key: 'location', width: 18 },
  { header: 'Membership No', key: 'barcodeMembershipNo', width: 16 },
  { header: 'Member Category', key: 'memberStatus', width: 16, hint: 'e.g. NRI' },
  { header: 'Life Status', key: 'status', width: 13, options: LIFE_STATUSES },
  { header: 'Date of Marriage', key: 'dateOfMarriage', width: 16, text: true, hint: 'YYYY-MM-DD' },
  { header: 'Date of Death', key: 'dateOfDeath', width: 15, text: true, hint: 'YYYY-MM-DD' },
  { header: 'Transferred To', key: 'transferChurch', width: 22 },
  // A guest stay (Member Category "Guest"): when it began and is expected to
  // end, and where they came from. Blank for everyone on the roll.
  { header: 'Guest Since', key: 'guestFrom', width: 14, text: true, hint: 'YYYY-MM-DD, guests only' },
  { header: 'Guest Until', key: 'guestUntil', width: 14, text: true, hint: 'YYYY-MM-DD, blank = open' },
  { header: 'Guest Home Church', key: 'guestOrigin', width: 22 },
  { header: 'Member Since', key: 'membershipDate', width: 14, text: true, hint: 'YYYY-MM-DD' },
  { header: 'Baptism Date', key: 'baptismDate', width: 15, text: true, hint: 'YYYY-MM-DD' },
  { header: 'Confirmation Date', key: 'confirmationDate', width: 17, text: true, hint: 'YYYY-MM-DD' },
  { header: 'Communion Date', key: 'communionDate', width: 16, text: true, hint: 'YYYY-MM-DD' },
  { header: 'Communion Status', key: 'communionStatus', width: 18, options: COMMUNION_STATUSES }
] as const;

/**
 * Sheet 1 — one row per household, exactly what the 👤 Head form holds.
 *
 * Mobile and Address are required here and nowhere else: the mobile becomes the
 * household's contact number and the address is the household's, so the Head
 * form requires both for the same reason.
 */
export const HEAD_COLUMNS: readonly SheetColumn[] = [
  { header: 'Member ID', key: 'memberId', width: 14, hint: 'Blank creates a new person' },
  { header: 'Family ID', key: 'familyId', width: 14, hint: 'Blank creates a new household' },
  { header: 'Family Key', key: 'familyKey', width: 12, required: true,
    hint: 'Your own label, e.g. F1' },
  { header: 'Full Name', key: 'name', width: 26, required: true },
  { header: 'Gender', key: 'gender', width: 11, required: true, options: GENDERS },
  { header: 'Date of Birth', key: 'dateOfBirth', width: 15, required: true, text: true,
    hint: 'YYYY-MM-DD' },
  { header: 'Marital Status', key: 'maritalStatus', width: 16, options: MARITAL_STATUSES },
  { header: 'Mobile', key: 'mobileNumber', width: 14, required: true, text: true,
    hint: "7 to 12 digits · becomes the household's contact" },
  { header: 'Address', key: 'address', width: 34, required: true, household: true },
  { header: 'Parish', key: 'parish', width: 18, household: true },
  { header: 'Family Status', key: 'familyStatus', width: 15, household: true,
    options: FAMILY_STATUSES },
  ...PERSON_TAIL
] as const;

/** Sheet 2 — one row per person who is not a head, the 👥 Member form. */
export const MEMBER_COLUMNS: readonly SheetColumn[] = [
  { header: 'Member ID', key: 'memberId', width: 14, hint: 'Blank creates a new person' },
  { header: 'Family Key', key: 'familyKey', width: 12, required: true,
    hint: 'Must match a row on Heads' },
  { header: 'Full Name', key: 'name', width: 26, required: true },
  { header: 'Relation', key: 'relationToHead', width: 14, required: true, options: RELATIONS },
  { header: 'Gender', key: 'gender', width: 11, required: true, options: GENDERS },
  { header: 'Date of Birth', key: 'dateOfBirth', width: 15, required: true, text: true,
    hint: 'YYYY-MM-DD' },
  { header: 'Marital Status', key: 'maritalStatus', width: 16, options: MARITAL_STATUSES },
  { header: 'Mobile', key: 'mobileNumber', width: 14, text: true, hint: '7 to 12 digits' },
  ...PERSON_TAIL
] as const;

/** The Reference sheet: one column per dropdown. */
export const REFERENCE_LISTS: ReadonlyArray<{ header: string; values: readonly string[] }> = [
  { header: 'Gender', values: GENDERS },
  { header: 'Relation (Members only)', values: RELATIONS },
  { header: 'Marital Status', values: MARITAL_STATUSES },
  { header: 'Blood Group', values: BLOOD_GROUPS },
  { header: 'Life Status', values: LIFE_STATUSES },
  { header: 'Communion Status', values: COMMUNION_STATUSES },
  { header: 'Family Status', values: FAMILY_STATUSES }
] as const;

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

const field = (source: unknown, key: string): string =>
  text((source as Record<string, unknown> | null | undefined)?.[key]);

/**
 * The key that ties a Members row to its household.
 *
 * Built from the row id, which is the primary key, so two households can never
 * share one. Deriving it from the familyId text looked friendlier but could
 * collide — DEMO-FAM-000 and FAM-000 both reduce to F000 — and a repeated key
 * makes the file unimportable, since a key used twice is refused.
 */
export const familyKeyFor = (family: Family): string =>
  family?.id !== undefined && family?.id !== null
    ? `F${family.id}`
    : `F${text(family?.familyId).replace(/[^A-Za-z0-9]/g, '')}`;

/** True for the one member of a household whose relation is Head. */
export const isHead = (member: Member): boolean =>
  String(member?.relationToHead || '').trim().toLowerCase() === 'head';

/**
 * One Heads row: the head's own fields, plus the household's.
 *
 * `head` may be missing for a household whose head record was never created —
 * the row is still written so the household is not silently dropped from the
 * file, and the import will ask for the missing details.
 */
export const headToRow = (family: Family, head?: Member): string[] =>
  HEAD_COLUMNS.map((c) => {
    if (c.key === 'familyKey') return familyKeyFor(family);
    if (c.key === 'familyId') return text(family.familyId) || `FAM-${family.id}`;
    if (c.key === 'familyStatus') return text(family.status);
    if (c.household) return field(family, c.key);
    // The household's contact is the head's own mobile, so fall back to it when
    // the head record is missing.
    if (c.key === 'mobileNumber') {
      return field(head, 'mobileNumber') || text(family.contact) || text(family.contactNumber);
    }
    if (c.key === 'name') return field(head, 'name') || text(family.headOfFamily);
    if (c.key === 'gender') return field(head, 'gender') || text(family.headGender);
    return field(head, c.key);
  });

/** One Members row, for someone who is not a head. */
export const memberToRow = (member: Member, familyKey: string): string[] =>
  MEMBER_COLUMNS.map((c) => (c.key === 'familyKey' ? familyKey : field(member, c.key)));

/** Header text -> column, for reading an uploaded file back. Case-insensitive. */
export const columnsByHeader = (columns: readonly SheetColumn[]): Map<string, SheetColumn> =>
  new Map(columns.map((c) => [c.header.trim().toLowerCase(), c]));
